/**
 * Разбор `scores_by_case` — скоров по каждому датасету метадатасета.
 *
 * Бекенд отдаёт их с самого появления мультидатасетных прогонов, но никто не
 * читал: в таблице виден только агрегат (среднее без весов по датасетам), а по
 * нему не понять, какой датасет тянет вниз. Разброс между датасетами внутри
 * одного сабмишена бывает больше, чем разброс между алгоритмами.
 */

export type ScoreSplit = 'train' | 'validation' | 'test';

export interface CaseUsage {
  /** Токены датасета — знаменатель коэффициента. */
  datasetTokens: number | null;
  /** Токены, потраченные на этот датасет. */
  spentTokens: number | null;
  /** Расход, делённый на размер датасета. Тот же смысл, что у общего
   *  коэффициента, но видно, какой датасет дорогой относительно своего
   *  размера, а не в абсолютных числах. */
  coefficient: number | null;
}

export interface CaseScores {
  /** `dataset_source:dataset_id` — так его собирают и бекенд, и ML. */
  caseId: string;
  /** То, что показываем человеку: идентификатор без префикса источника. */
  label: string;
  source: string | null;
  train: number | null;
  validation: number | null;
  test: number | null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Разложить `case_id` на источник и идентификатор датасета.
 *
 *  Делим по ПЕРВОМУ двоеточию: в идентификаторе датасета оно тоже может
 *  встретиться, и деление по последнему разорвало бы его посередине. */
export function splitCaseId(caseId: string): { source: string | null; label: string } {
  const at = caseId.indexOf(':');
  if (at <= 0) return { source: null, label: caseId };
  return { source: caseId.slice(0, at), label: caseId.slice(at + 1) };
}

export function parseScoresByCase(raw: unknown): CaseScores[] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const out: CaseScores[] = [];
  for (const [caseId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const splits = value as Record<string, unknown>;
    const train = numberOrNull(splits.train);
    const validation = numberOrNull(splits.validation);
    const test = numberOrNull(splits.test);
    // Кейс без единого числа показывать нечем: пустая строка таблицы только
    // мешает читать остальные.
    if (train === null && validation === null && test === null) continue;
    const { source, label } = splitCaseId(caseId);
    out.push({ caseId, label, source, train, validation, test });
  }
  // Порядок ключей в объекте не гарантирован, а таблица должна быть
  // воспроизводимой между открытиями.
  return out.sort((a, b) => a.caseId.localeCompare(b.caseId));
}

/** Среднее без весов — тот же способ, которым ML считает ранжирующий скор.
 *  Показывается рядом с колонкой, чтобы было видно, из чего сложился агрегат. */
export function meanAcrossCases(cases: CaseScores[], split: ScoreSplit): number | null {
  const values = cases.map((item) => item[split]).filter((v): v is number => v !== null);
  if (!values.length) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}


function tokensOf(bucket: unknown): number | null {
  if (!bucket || typeof bucket !== 'object') return null;
  const tokens = (bucket as Record<string, unknown>).tokens;
  if (!tokens || typeof tokens !== 'object') return null;
  return numberOrNull((tokens as Record<string, unknown>).total_tokens);
}

/** Расход и коэффициент по каждому датасету.
 *
 *  Оба слагаемых уже приходят с сервера: `usage_by_case[case].total` — сколько
 *  на датасет потрачено, `dataset_tokens.by_case[case]` — сколько в нём
 *  токенов. Их частное и есть коэффициент этого датасета; в таблице виден
 *  только общий, а он не отвечает на вопрос, какой датасет дорогой.
 *
 *  Делит только когда знаменатель положительный: ноль токенов датасета
 *  означает «размер неизвестен», и бесконечность на экране была бы хуже
 *  прочерка. */
export function parseUsageByCaseTotals(usageByCase: unknown, datasetTokens: unknown): Record<string, CaseUsage> {
  const out: Record<string, CaseUsage> = {};
  const byCase =
    usageByCase && typeof usageByCase === 'object' && !Array.isArray(usageByCase)
      ? (usageByCase as Record<string, unknown>)
      : {};
  const tokensRaw =
    datasetTokens && typeof datasetTokens === 'object' && !Array.isArray(datasetTokens)
      ? ((datasetTokens as Record<string, unknown>).by_case as Record<string, unknown> | undefined)
      : undefined;

  // Ключи из обеих половин, без повторов: датасет может быть в одной и
  // отсутствовать в другой, и терять его из-за этого нельзя. Через объект, а
  // не Set: цель TypeScript в проекте не разрешает обход Set.
  const seen: Record<string, true> = {};
  for (const key of Object.keys(byCase)) seen[key] = true;
  for (const key of Object.keys(tokensRaw ?? {})) seen[key] = true;

  for (const caseId of Object.keys(seen)) {
    const spentTokens = tokensOf((byCase[caseId] as Record<string, unknown> | undefined)?.total);
    const datasetTokensValue = numberOrNull(tokensRaw?.[caseId]);
    const coefficient =
      spentTokens !== null && datasetTokensValue !== null && datasetTokensValue > 0
        ? spentTokens / datasetTokensValue
        : null;
    out[caseId] = { datasetTokens: datasetTokensValue, spentTokens, coefficient };
  }
  return out;
}

/** Дельта сплита: «после» минус «до».
 *
 *  Считается на фронте, а не берётся готовой, потому что бэкенд отдаёт блоки
 *  замеров, а не их разности, и одинаково для всех трёх сплитов. */
export function splitDelta(after: unknown, before: unknown): number | null {
  const a = numberOrNull((after as Record<string, unknown> | undefined)?.score);
  const b = numberOrNull((before as Record<string, unknown> | undefined)?.score);
  if (a === null || b === null) return null;
  return a - b;
}
